import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Calendar, ArrowLeft, Save, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useApi } from '@/hooks/useAuth'
import type { Memory } from '@/types/memory'
import type { Person } from '@/types/person'

const memorySchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().min(1).max(5000),
  date: z.string().min(1),
})

type MemoryForm = z.infer<typeof memorySchema>

export default function MemoryDisplayPage() {
  const { id } = useParams<{ id: string }>()
  const [memory, setMemory] = useState<Memory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [people, setPeople] = useState<Person[]>([])
  const [selectedPeople, setSelectedPeople] = useState<string[]>([])
  const { getMemory, updateMemory, getPeople } = useApi()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
    reset,
  } = useForm<MemoryForm>({
    resolver: zodResolver(memorySchema),
  })

  useEffect(() => {
    if (!id) return
    getMemory(id)
      .then(setMemory)
      .catch(() => setError('Failed to load memory'))
      .finally(() => setLoading(false))
  }, [id])

  useEffect(() => {
    getPeople().then(setPeople).catch(() => setPeople([]))
  }, [getPeople])

  useEffect(() => {
    if (memory) {
      reset({ name: memory.name, description: memory.description, date: memory.date.split('T')[0] })
      setSelectedPeople(memory.people || [])
    }
  }, [memory, reset])

  const togglePerson = (personId: string) => {
    setSelectedPeople(prev =>
      prev.includes(personId) ? prev.filter(id => id !== personId) : [...prev, personId]
    )
  }

  const isPeopleDirty = JSON.stringify(selectedPeople.sort()) !== JSON.stringify((memory?.people || []).sort())
  const isFormDirty = isDirty || isPeopleDirty

  const onSubmit = async (data: MemoryForm) => {
    if (!id) return
    try {
      setError('')
      await updateMemory(id, { ...data, people: selectedPeople })
      navigate('/memories')
    } catch {
      setError('Failed to update memory')
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Loading memory...</p>
      </div>
    )
  }

  if (!memory) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Memory not found</p>
        <Button variant="outline" onClick={() => navigate('/memories')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Memories
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/memories')} className="mb-6">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Memories
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Memory Details</CardTitle>
          <CardDescription className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(memory.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {error && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">{error}</div>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">Title</Label>
              <Input id="name" {...register('name')} />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input id="date" type="date" {...register('date')} />
              {errors.date && <p className="text-sm text-destructive">{errors.date.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" className="min-h-[200px]" {...register('description')} />
              {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
            </div>

            {people.length > 0 && (
              <div className="space-y-2">
                <Label>People</Label>
                <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
                  {selectedPeople.length === 0 && (
                    <span className="text-muted-foreground text-sm">Select people associated with this memory</span>
                  )}
                  {selectedPeople.map(id => {
                    const person = people.find(p => p.id === id)
                    if (!person) return null
                    return (
                      <Badge key={id} variant="secondary" className="pl-2 pr-1 py-1">
                        {person.name}
                        <button
                          type="button"
                          onClick={() => togglePerson(id)}
                          className="ml-1 hover:bg-destructive/20 rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    )
                  })}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {people
                    .filter(p => !selectedPeople.includes(p.id))
                    .map(person => (
                      <Badge
                        key={person.id}
                        variant="outline"
                        className="cursor-pointer hover:bg-secondary"
                        onClick={() => togglePerson(person.id)}
                      >
                        + {person.name}
                      </Badge>
                    ))}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Button type="submit" disabled={isSubmitting || !isFormDirty}>
                <Save className="mr-2 h-4 w-4" />
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/memories')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}